import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, type BrickowlPriceRefreshResult, type ItemMarketplaces, type MarketFigures, type MarketplaceStatus } from '../api';
import { anyCurrencyUnstated, ageDays, visibleRows } from '../lib/marketplaces';
import { Badge, Button, Card, Empty, ErrorMessage, Notice, Spinner, number, unitMoney, unitPlain, when } from '../ui';
import { ColorSwatch } from './ItemImage';

const COND = { N: 'New', U: 'Used' } as const;
const STALE_DAYS = 14;

/** One marketplace's figure: the headline price large, the spread and the number of lots beneath it. */
function Figure({ f, headline, lots }: { f: MarketFigures | null; headline: 'avg' | 'min'; lots: string }) {
  if (f === null) return <span className="text-slate-300">—</span>;
  if (f.status === 'unavailable') {
    return (
      <span className="text-xs text-slate-400" title={f.note ?? 'The marketplace has nothing for this one'}>
        n/a
      </span>
    );
  }
  const main = headline === 'avg' ? f.avgPrice : f.minPrice;
  if (main === null) return <span className="text-xs text-slate-500" title={`Checked ${when(f.fetchedAt)}`}>none listed</span>;
  const stale = ageDays(f.fetchedAt) > STALE_DAYS;
  // A marketplace that does not say which currency it means gets no currency symbol, only a dagger for the footnote.
  const ccy = f.currency ?? undefined;
  const show = (v: string | null) => (f.currency === null ? `${unitPlain(v)}†` : unitMoney(v, ccy));
  return (
    <span className={stale ? 'opacity-60' : undefined} title={`${when(f.fetchedAt)}${stale ? ' (out of date)' : ''}`}>
      <span className="font-medium tabular-nums">{show(main)}</span>
      <span className="block text-xs text-slate-500 tabular-nums">
        {f.minPrice !== null && f.maxPrice !== null && f.minPrice !== f.maxPrice ? `${show(f.minPrice)}–${show(f.maxPrice)} · ` : ''}
        {f.lotCount !== null ? `${number(f.lotCount)} ${lots}` : ''}
      </span>
    </span>
  );
}

function Head({ m, children }: { m: MarketplaceStatus; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span>{m.name}</span>
      {m.state === 'off' && <Badge>Not connected</Badge>}
      {m.state === 'coming' && <Badge tone="slate">Coming</Badge>}
      {children}
    </div>
  );
}

/**
 * What each marketplace says an item costs, side by side, one row per colour and condition. It shows what has been
 * fetched and kept: opening an item asks nobody for anything. Brick Owl's figures are fetched with the Refresh
 * button, which spends part of Brick Owl's daily allowance. eBay has its place already and fills in when it is built.
 */
export function MarketplaceCompare({ itemId, inUse, canRefresh }: { itemId: number; inUse: Set<number>; canRefresh: boolean }) {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const q = useQuery({ queryKey: ['item-marketplaces', itemId], queryFn: () => api<ItemMarketplaces>(`/api/v1/items/${itemId}/marketplaces`) });
  const refresh = useMutation({
    mutationFn: (force: boolean) => api<BrickowlPriceRefreshResult>(`/api/v1/items/${itemId}/marketplaces/brickowl/refresh`, { body: { force } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['item-marketplaces', itemId] }),
  });

  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <ErrorMessage error={q.error} />;
  const data = q.data;
  const [bl, bo, ebay] = data.marketplaces as [MarketplaceStatus, MarketplaceStatus, MarketplaceStatus];
  const { rows, hidden } = visibleRows(data, inUse, showAll);
  const r = refresh.data?.result;
  const unstated = anyCurrencyUnstated(rows, (x) => x.brickowl.forSale);

  return (
    <Card
      title="Pricing across marketplaces"
      actions={
        bo.state === 'ready' && canRefresh ? (
          <Button small variant="secondary" disabled={refresh.isPending} onClick={() => refresh.mutate(false)}>
            {refresh.isPending ? 'Asking Brick Owl…' : 'Refresh Brick Owl prices'}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-3">
        <ErrorMessage error={refresh.error} />
        {r && (
          <Notice tone={r.stoppedBecause || r.unmapped.length > 0 ? 'amber' : 'green'}>
            Looked up {r.looked} {r.looked === 1 ? 'colour' : 'colours'} on Brick Owl
            {r.fresh > 0 && `, kept ${r.fresh} fetched in the last few hours`}.
            {r.remaining > 0 && ` ${r.remaining} more are left: press Refresh again.`}
            {r.unmapped.length > 0 && ` Could not look up ${r.unmapped.map((u) => `${u.colorName} (${u.reason})`).slice(0, 3).join('; ')}.`}
            {r.stoppedBecause && ` Stopped: ${r.stoppedBecause}`}
          </Notice>
        )}
        {bl.state === 'off' && bo.state === 'off' && (
          <Notice>
            No marketplace is connected, so there is nothing to compare.{' '}
            <Link to="/channels" className="font-medium underline">
              Connect BrickLink or Brick Owl
            </Link>
            .
          </Notice>
        )}
        {rows.length === 0 ? (
          <Empty>No colours yet. Add colours to this item and their prices appear here.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="data w-full min-w-[56rem] text-sm">
              <thead>
                <tr>
                  <th rowSpan={2}>Colour</th>
                  <th rowSpan={2}>Cond.</th>
                  <th colSpan={2} className="border-l border-slate-200 text-left">
                    <Head m={bl} />
                  </th>
                  <th className="border-l border-slate-200 text-left">
                    <Head m={bo} />
                  </th>
                  <th className="border-l border-slate-200 text-left">
                    <Head m={ebay} />
                  </th>
                </tr>
                <tr className="text-xs font-normal text-slate-500">
                  <th className="border-l border-slate-200 text-left font-normal">Sold, last 6 months (average)</th>
                  <th className="text-left font-normal">For sale now (average)</th>
                  <th className="border-l border-slate-200 text-left font-normal">For sale now (lowest)</th>
                  <th className="border-l border-slate-200 text-left font-normal">For sale now</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.colorId}-${row.condition}`}>
                    <td>
                      <span className="flex items-center gap-2">
                        {row.colorRgb && <ColorSwatch rgb={row.colorRgb} name="" />}
                        {row.colorName}
                      </span>
                    </td>
                    <td>{COND[row.condition]}</td>
                    <td className="border-l border-slate-100">
                      <Figure f={row.bricklink.sold} headline="avg" lots="sales" />
                    </td>
                    <td>
                      <Figure f={row.bricklink.forSale} headline="avg" lots="lots" />
                    </td>
                    <td className="border-l border-slate-100">
                      <Figure f={row.brickowl.forSale} headline="min" lots="lots" />
                    </td>
                    <td className="border-l border-slate-100">
                      <Figure f={row.ebay.forSale} headline="min" lots="listings" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span>
            BrickLink: {data.region === 'worldwide' ? 'all sellers' : `sellers in ${data.region}`}. Brick Owl: lots listed now for delivery to the UK. eBay is not connected yet.
            {unstated && ' † Brick Owl does not say which currency its prices are in, so no symbol is shown; check one against a listing before relying on them.'}
          </span>
          {(hidden > 0 || showAll) && (
            <Button small variant="ghost" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Only colours in use' : `Show all colours (${hidden} more)`}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
